import PropTypes from "prop-types";
import React, { useContext, useState } from "react";

import { capitalizeFirstCharacter, classNames } from "@swa-ui/string";

import globalRules from "../assets/styles/globalRules.module.scss";
import { getComponentColor } from "../getComponentColor";
import { Icon } from "../Icon";
import styles from "./Text.module.scss";
import { TextContext, TextProvider } from "./TextProvider";

const DEFAULT_SHRINK_SIZE = 0.75;

/**
 * Text accepts typography attributes as props, and will size according to props given.
 */
export const Text = (props) => {
  const {
    bold,
    casing,
    children,
    className,
    color,
    element = "div",
    isSubScript = false,
    isSuperScript = false,
    italics = false,
    prefixIcon,
    size: propSize,
    suffixIcon,
    textType: propTextType,
  } = props;
  const HtmlElement = element;
  const { size: contextSize, textType: contextTextType } = useContext(TextContext);
  const [iconSize, setIconSize] = useState("0");
  const size = propSize ?? contextSize;
  const textType = propTextType ?? contextTextType;

  return (
    <HtmlElement {...getProps()}>
      {renderIcon(prefixIcon)}
      <TextProvider size={size} textType={textType}>
        {children}
      </TextProvider>
      {renderIcon(suffixIcon)}
    </HtmlElement>
  );

  function renderIcon(iconFromProps) {
    const iconProps = getIconProps(iconFromProps);

    return (
      iconFromProps && (
        <span className={styles.icon}>
          <Icon {...iconProps} />
        </span>
      )
    );
  }

  function getProps() {
    return {
      "aria-label": props["aria-label"],
      className: getClass(),
      ref: getCallbackRef(),
      style: {
        color: getComponentColor(color),
      },
    };
  }

  function getIconProps(iconFromProps) {
    const isIconShorthand = typeof iconFromProps === "string";

    return {
      color: getComponentColor(color),
      size: iconSize,
      ...(isIconShorthand ? { name: iconFromProps, shrink: DEFAULT_SHRINK_SIZE } : iconFromProps),
    };
  }

  function getCallbackRef() {
    return (elementRef) => elementRef && setIconSize(`${getComputedStyle(elementRef).lineHeight}`);
  }

  function getClass() {
    return classNames(className, globalRules[getTextTypeClass()], {
      [styles[casing]]: casing,
      [styles.fontWeightBold]: bold,
      [styles.italics]: italics,
    });
  }

  function getTextTypeClass() {
    return `${textType}${capitalizeFirstCharacter(size)}${getTextTypeScriptClass()}`;
  }

  function getTextTypeScriptClass() {
    return isSuperScript ? "Superscript" : isSubScript ? "Subscript" : "";
  }
};

Text.displayName = "Text";

export const textPropTypes = {
  /** Text used for accessibile description of child content. */
  "aria-label": PropTypes.string,

  /** Indicates if bold font style should be applied. */
  bold: PropTypes.bool,

  /** Case transformation applied to text content. */
  casing: PropTypes.oneOf(["sentence", "uppercase"]),

  /** Content that will be rendered in static text. */
  children: PropTypes.node,

  /**
   * Additional classes for positioning the component. Given classes may only position this component
   * for layout purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /** Color provided to both text and icons. */
  color: PropTypes.string,

  /** Optional html element type used to wrap the child content, such as 'span.' */
  element: PropTypes.string,

  /** Indicates if subscript styling should be applied. */
  isSubScript: PropTypes.bool,

  /** Indicates if superscript styling should be applied. */
  isSuperScript: PropTypes.bool,

  /** Indicates if italics font style should be applied */
  italics: PropTypes.bool,

  /**
   * Icon to be rendered before the text. Can be shorthand string for the name, and the icon will
   * automatically adjust its size and color to match the text.
   */
  prefixIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),

  /** T-shirt size of styling that should be applied, such as "large" or "small." */
  size: PropTypes.oneOf(["large", "medium", "small", "xlarge", "xsmall"]),

  /**
   * Icon to be rendered after the text. Can be shorthand string for the name, and the icon will
   * automatically adjust its size and color to match the text.
   */
  suffixIcon: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),

  /** Indicates which typography group styling should be applied, such as "body" or "display." */
  textType: PropTypes.oneOf(["body", "details", "display", "label", "legal"]),
};

Text.propTypes = textPropTypes;
